import React, { useEffect, useState } from 'react';
import {
  Stack,
  Grid,
  Text,
  Badge,
  Group,
  Progress,
  SimpleGrid,
  Paper,
  Button,
  Alert,
  Container,
  Divider,
  Pagination,
  Box,
  Select,
  Menu,
  ActionIcon
} from '@mantine/core';
import { Modal } from '@mantine/core';
import {
  IconCalendarClock,
  IconCalendarCheck,
  IconCalendarX,
  IconTrendingUp,
  IconUsers,
  IconClipboardList,
  IconAlertCircle,
  IconDownload,
  IconDots,
  IconFilter
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import PageTitle from 'components/PageTitle';
import Card from 'components/Card';
import DataTables, { DataTableColumn } from 'components/DataTables';
import Dates from 'components/Dates';
import { ComparisonAnalysisList } from 'components/LIA/ComparisonAnalysis';
import {
  overviewService,
  AssessmentScheduleOverview,
  OverviewFilters
} from 'services/lia/overviewService';
import OverviewFilter from './components/OverviewFilter';
import { OverviewSkeleton } from './components/OverviewSkeleton';
import ComparisonDetailModal from './components/ComparisonDetailModal';
import { useMediaQuery } from '@mantine/hooks';

type AssessmentSchedule = AssessmentScheduleOverview;
type AssessmentStatus = 'All' | 'Planned' | 'On Going' | 'Completed' | 'Incomplete';

const LIAOverview: React.FC = () => {
  /* eslint-disable */
  const navigate = useNavigate();

  const [assessmentListPage, setAssessmentListPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [incompletePage, setIncompletePage] = useState(1);
  const [recentPage, setRecentPage] = useState(1);

  const [isTypeFilterPressed, setIsTypeFilterPressed] = useState(false);
  const [filterModalOpened, setFilterModalOpened] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const [selectedAssessmentStatus, setSelectedAssessmentStatus] = useState<AssessmentStatus>('All');

  const [comparisonModalOpened, setComparisonModalOpened] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState<{
    beforeId: string;
    afterId: string;
  } | null>(null);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const getDefault30DaysFilter = (): OverviewFilters => ({
    startDate: dayjs().subtract(30, 'day').toDate(),
    endDate: new Date(),
    preset: '30'
  });

  const [filters, setFilters] = useState<OverviewFilters>(getDefault30DaysFilter);
  const [appliedFilters, setAppliedFilters] = useState<OverviewFilters>(getDefault30DaysFilter);
  const pageSize = 5;

  const USE_MOCK_DATA = false;
  /* eslint-enable */

  const {
    data: apiOverviewData,
    isLoading: apiIsLoading,
    error: apiError,
    refetch
  } = useQuery({
    queryKey: ['lia-overview', appliedFilters],
    queryFn: () => overviewService.getOverview(appliedFilters),
    refetchInterval: 30000,
    staleTime: 10000,
    enabled: !USE_MOCK_DATA
  });

  const overviewData = apiOverviewData;
  const isLoading = apiIsLoading;
  const error = apiError;

  const handleFiltersChange = (newFilters: OverviewFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = (filtersToApply: OverviewFilters) => {
    setFilters(filtersToApply);
    setAppliedFilters(filtersToApply);
    setAssessmentListPage(1);
    setUpcomingPage(1);
    setIncompletePage(1);
    setRecentPage(1);
  };

  useEffect(() => {
    if (filters.preset) {
      setAppliedFilters(filters);
      setAssessmentListPage(1);
      setUpcomingPage(1);
      setIncompletePage(1);
      setRecentPage(1);
    }
  }, [filters.preset]);

  const handleView = (data: AssessmentSchedule) => {
    navigate(`/app/lia/assessment-schedules/detail/${data.msAssessmentScheduleID}`);
  };

  const getFilteredAssessments = (): AssessmentSchedule[] => {
    if (!overviewData) return [];

    let allAssessments = [
      ...overviewData.ongoingAssessments,
      ...overviewData.upcomingAssessments,
      ...overviewData.completedAssessments,
      ...overviewData.incompleteAssessments,
      ...overviewData.recentAssessments
    ];

    const uniqueAssessments = allAssessments.reduce((acc, current) => {
      const exists = acc.find(
        (item) => item.msAssessmentScheduleID === current.msAssessmentScheduleID
      );
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, [] as AssessmentSchedule[]);

    let filteredAssessments = uniqueAssessments;

    if (isTypeFilterPressed && selectedType) {
      if (selectedType === 'Product') {
        filteredAssessments = filteredAssessments.filter(
          (assessment) => assessment.type.toLowerCase() === 'product'
        );
      } else if (selectedType === 'Non-Product') {
        filteredAssessments = filteredAssessments.filter(
          (assessment) =>
            assessment.type.toLowerCase() === 'non-product' ||
            assessment.type.toLowerCase() === 'nonproduct' ||
            assessment.type.toLowerCase() !== 'product'
        );
      }
    }

    if (selectedAssessmentStatus !== 'All') {
      filteredAssessments = filteredAssessments.filter(
        (assessment) => assessment.status === selectedAssessmentStatus
      );
    }

    return filteredAssessments;
  };

  const handleViewComparison = async (beforeId: string, afterId: string) => {
    setSelectedComparison({ beforeId, afterId });
    setComparisonModalOpened(true);
    setIsLoadingComparison(true);

    try {
      const data = await overviewService.getAssessmentComparison(beforeId, afterId);
      setComparisonData(data);
    } catch {
      new Error('Failed to load comparison data, please try again later. ');
      setComparisonData(null);
    } finally {
      setIsLoadingComparison(false);
    }
  };

  const handleStatusCardClick = (status: AssessmentStatus) => {
    setSelectedAssessmentStatus(status);
    setAssessmentListPage(1);
  };

  const handleDownloadExcel = async () => {
    try {
      const filteredData = getFilteredAssessments();

      const filterLabels: string[] = [];

      if (isTypeFilterPressed && selectedType) {
        filterLabels.push(selectedType.toLowerCase());
      }

      if (selectedAssessmentStatus !== 'All') {
        filterLabels.push(selectedAssessmentStatus.toLowerCase().replace(' ', '_'));
      }

      const filterSuffix = filterLabels.length > 0 ? `_${filterLabels.join('_')}` : '_all';
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `assessments${filterSuffix}_${dateStr}.xlsx`;

      await downloadFilteredAssessments(filteredData, filename);
    } catch {
      throw new Error('Failed to download assessments, please try again later.');
    }
  };

  const downloadFilteredAssessments = async (data: AssessmentSchedule[], filename: string) => {
    // Create workbook data
    const worksheetData = data.map((assessment, index) => ({
      'No.': index + 1,
      'Assessment Name': assessment.name,
      Type: assessment.type,
      'Program ID': assessment.programID,
      'Program Name': assessment.programName || '',
      Status: assessment.status,
      'Start Time': new Date(assessment.startTime).toLocaleDateString(),
      'End Time': new Date(assessment.endTime).toLocaleDateString(),
      'Notification Date': assessment.notificationDate
        ? new Date(assessment.notificationDate).toLocaleDateString()
        : '',
      'Total Participants': assessment.totalParticipants,
      'Completed Participants': assessment.completedParticipants,
      'Total Respondents': assessment.totalRespondents,
      'Completed Respondents': assessment.completedRespondents,
      'Progress (%)': assessment.progressPercentage,
      'Completion Rate (%)': assessment.completionRate,
      'Days Remaining': assessment.daysRemaining,
      'Is Before Schedule': assessment.isBeforeSchedule ? 'Yes' : 'No',
      'Previous Schedule ID': assessment.previousScheduleID || '',
      'Created Date': new Date(assessment.createdDate).toLocaleDateString(),
      'Last Activity': new Date(assessment.lastActivity).toLocaleDateString()
    }));

    // Convert to CSV (simple approach) or use a library like xlsx for actual Excel format
    const csvContent = convertToCSV(worksheetData);
    downloadCSV(csvContent, filename.replace('.xlsx', '.csv'));
  };

  // Helper function to convert data to CSV
  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','), // Header row
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            // Escape commas and quotes in values
            return typeof value === 'string' && (value.includes(',') || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          })
          .join(',')
      )
    ];

    return csvRows.join('\n');
  };

  // Helper function to download CSV
  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Planned':
        return 'blue';
      case 'On Going':
        return 'green';
      case 'Completed':
        return 'teal';
      case 'Incomplete':
        return 'red';
      default:
        return 'gray';
    }
  };

  const StatCard = ({
    title,
    value,
    total,
    icon,
    color,
    subtitle,
    onClick,
    clickable = false
  }: {
    title: string;
    value: string | number;
    total?: number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
    onClick?: () => void;
    clickable?: boolean;
  }) => {
    const percentage = total && total > 0 ? Math.round((Number(value) / total) * 100) : 0;
    const displayValue = total ? `${value} (${percentage}%)` : value;

    return (
      <Paper
        withBorder
        p='md'
        radius='lg'
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid #e9ecef',
          transition: 'all 0.2s ease',
          position: 'relative',
          overflow: 'hidden',
          cursor: clickable ? 'pointer' : 'default'
        }}
        className={clickable ? 'stat-card-hover clickable-card' : 'stat-card-hover'}
        onClick={clickable ? onClick : undefined}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '60px',
            height: '60px',
            background: `linear-gradient(135deg, var(--mantine-color-${color}-1) 0%, var(--mantine-color-${color}-2) 100%)`,
            borderRadius: '0 0 0 100%',
            opacity: 0.3
          }}
        />
        <Group justify='space-between' align='flex-start'>
          <Stack gap='xs' style={{ flex: 1, zIndex: 1 }}>
            <Text c='dimmed' size='sm' fw={'bold'} tt='uppercase' lts={0.5}>
              {title}
            </Text>
            <Text fw={700} size='2rem' c='dark' lh={1}>
              {displayValue}
            </Text>
            {subtitle && (
              <Text c='dimmed' size='xs'>
                {subtitle}
              </Text>
            )}
          </Stack>
          <Box
            style={{
              padding: '8px',
              background: `var(--mantine-color-${color}-1)`,
              borderRadius: '8px',
              color: `var(--mantine-color-${color}-6)`,
              zIndex: 1
            }}
          >
            {icon}
          </Box>
        </Group>
      </Paper>
    );
  };

  const AssessmentTable = ({
    title,
    showProgress = false,
    currentPage,
    onPageChange,
    showStatusFilter = false,
    showDownload = false
  }: {
    /* eslint-disable */
    title: string;
    showProgress?: boolean;
    currentPage: number;
    onPageChange: (page: number) => void;
    showStatusFilter?: boolean;
    showDownload?: boolean;
    /* eslint-enable */
  }) => {
    const filteredAssessments = getFilteredAssessments();

    const totalPages = Math.ceil(filteredAssessments.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedData = filteredAssessments.slice(startIndex, startIndex + pageSize);

    const getTableTitle = () => {
      let titleText = title;
      const filters: string[] = [];

      if (isTypeFilterPressed && selectedType) {
        filters.push(selectedType);
      }

      if (selectedAssessmentStatus !== 'All') {
        filters.push(selectedAssessmentStatus);
      }

      if (filters.length > 0) {
        titleText += ` (${filters.join(' • ')})`;
      }

      return titleText;
    };

    const statusOptions = [
      { value: 'All', label: 'All Status' },
      { value: 'Planned', label: 'Planned' },
      { value: 'On Going', label: 'On Going' },
      { value: 'Completed', label: 'Completed' },
      { value: 'Incomplete', label: 'Incomplete' }
    ];

    const columns: DataTableColumn[] = [
      {
        heading: 'Assessment',
        props: (row: AssessmentSchedule) => (
          <Stack gap={4}>
            <Group gap='xs'>
              <Text fw={500} size='sm' lineClamp={1}>
                {row.name}
              </Text>
              {!row.isBeforeSchedule && (
                <Badge size='xs' color='orange' variant='light'>
                  Follow-up
                </Badge>
              )}
            </Group>
            {row.programName && (
              <Text size='xs' c='dimmed' lineClamp={1}>
                {row.programName}
              </Text>
            )}
          </Stack>
        ),
        sortable: true,
        id: 'name',
        active: true
      },
      {
        heading: 'Type',
        props: (row: AssessmentSchedule) => (
          <Badge variant='outline' size='sm' radius='sm'>
            {row.type}
          </Badge>
        ),
        sortable: true,
        id: 'type',
        active: true
      },
      {
        heading: 'Schedule',
        props: (row: AssessmentSchedule) => (
          <Stack gap={4}>
            <Text size='sm'>
              <Dates date={row.startTime} fz={12} />
            </Text>
            <Text size='xs' c='dimmed'>
              to <Dates date={row.endTime} fz={12} />
            </Text>
            {row.daysRemaining !== undefined && row.daysRemaining > 0 && (
              <Text size='xs' c='blue' fw={500}>
                {row.daysRemaining} days remaining
              </Text>
            )}
            {row.notificationDate && (
              <Text size='xs' c='orange' fw={500}>
                Notify: <Dates date={row.notificationDate} fz={10} />
              </Text>
            )}
          </Stack>
        ),
        sortable: true,
        id: 'startTime',
        active: true
      },
      {
        heading: 'Status',
        props: (row: AssessmentSchedule) => (
          <Badge color={getStatusColor(row.status)} variant='light' size='sm'>
            {row.status}
          </Badge>
        ),
        sortable: true,
        id: 'status',
        active: true
      }
    ];

    if (showProgress) {
      columns.push({
        heading: 'Progress',
        props: (row: AssessmentSchedule) => (
          <Stack gap={4}>
            <Progress
              value={row.progressPercentage}
              size='sm'
              color={row.progressPercentage === 100 ? 'teal' : 'blue'}
              radius='sm'
            />
            <Text size='xs' c='dimmed'>
              {row.completedRespondents}/{row.totalRespondents} responses
            </Text>
          </Stack>
        ),
        id: 'progress',
        active: true
      });
    }

    columns.push({
      heading: 'Participants',
      props: (row: AssessmentSchedule) => (
        <Stack gap={2}>
          <Text size='sm' fw={500}>
            {row.totalParticipants}
          </Text>
          <Text size='xs' c='dimmed'>
            {row.completedParticipants} completed
          </Text>
        </Stack>
      ),
      sortable: true,
      id: 'totalParticipants',
      active: true
    });

    columns.push({
      heading: 'Actions',
      props: 'action',
      id: 'actions',
      active: true
    });

    return (
      <Card>
        <Stack gap='md'>
          <Group justify='space-between' align='center'>
            <Group gap='sm'>
              <Text fw={600} size='lg' c='dark'>
                {getTableTitle()}
              </Text>
              {/* Show active filters as badges */}
              {(isTypeFilterPressed || selectedAssessmentStatus !== 'All') && (
                <Group gap='xs'>
                  {isTypeFilterPressed && selectedType && (
                    <Badge
                      variant='light'
                      color='blue'
                      size='sm'
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedType(null);
                        setIsTypeFilterPressed(false);
                        onPageChange(1);
                      }}
                    >
                      {selectedType} ×
                    </Badge>
                  )}
                  {selectedAssessmentStatus !== 'All' && (
                    <Badge
                      variant='light'
                      color='green'
                      size='sm'
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedAssessmentStatus('All');
                        onPageChange(1);
                      }}
                    >
                      {selectedAssessmentStatus} ×
                    </Badge>
                  )}
                </Group>
              )}
            </Group>

            <Group gap='sm'>
              {showStatusFilter && (
                <Select
                  placeholder='Filter by status'
                  value={selectedAssessmentStatus}
                  onChange={(value) => {
                    setSelectedAssessmentStatus(value as AssessmentStatus);
                    onPageChange(1);
                  }}
                  data={statusOptions}
                  size='sm'
                  style={{ minWidth: '150px' }}
                />
              )}

              {/* Add type filter dropdown */}
              <Select
                placeholder='Filter by type'
                value={isTypeFilterPressed ? selectedType : null}
                onChange={(value) => {
                  if (value) {
                    setSelectedType(value);
                    setIsTypeFilterPressed(true);
                  } else {
                    setSelectedType(null);
                    setIsTypeFilterPressed(false);
                  }
                  onPageChange(1);
                }}
                data={[
                  { value: 'Product', label: 'Product' },
                  { value: 'Non-Product', label: 'Non-Product' }
                ]}
                size='sm'
                style={{ minWidth: '120px' }}
                clearable
              />

              {showDownload && (
                <Menu shadow='md' width={250}>
                  <Menu.Target>
                    <ActionIcon variant='light' size='lg'>
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>
                      Download {filteredAssessments.length} assessment
                      {filteredAssessments.length !== 1 ? 's' : ''}
                      {isTypeFilterPressed && selectedType && (
                        <Text size='xs' c='dimmed'>
                          Type: {selectedType}
                        </Text>
                      )}
                      {selectedAssessmentStatus !== 'All' && (
                        <Text size='xs' c='dimmed'>
                          Status: {selectedAssessmentStatus}
                        </Text>
                      )}
                    </Menu.Label>
                    <Menu.Item
                      leftSection={<IconDownload size={14} />}
                      onClick={handleDownloadExcel}
                      disabled={filteredAssessments.length === 0}
                    >
                      Download Excel
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Group>

          {/* Show filter summary */}
          {filteredAssessments.length > 0 && (
            <Text size='sm' c='dimmed'>
              Showing {paginatedData.length} of {filteredAssessments.length} assessments
            </Text>
          )}

          <DataTables
            columns={columns}
            data={paginatedData}
            loading={false}
            isNumbering={false}
            withPagination={false}
            handleView={handleView}
            viewText='View Details'
            emptyMessageTitle={`No ${title.toLowerCase()}`}
            emptyMessageDescription={
              isTypeFilterPressed || selectedAssessmentStatus !== 'All'
                ? 'No assessments found matching the current filters'
                : 'No assessments found for this category'
            }
          />

          {totalPages > 1 && (
            <Group justify='center' mt='sm'>
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={onPageChange}
                size='sm'
                radius='md'
              />
            </Group>
          )}
        </Stack>
      </Card>
    );
  };

  if (error) {
    return (
      <Container fluid>
        <Stack gap='xl'>
          <PageTitle
            title='Assessment Overview'
            description='Monitor and manage your assessment schedules'
          />
          <OverviewFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApply={handleApplyFilters}
            isLoading={isLoading}
          />
          <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red'>
            Failed to load overview data. Please try again later.
            <Button
              variant='light'
              size='sm'
              onClick={() => {
                refetch();
                setAssessmentListPage(1);
                setUpcomingPage(1);
                setIncompletePage(1);
                setRecentPage(1);
              }}
              ml='md'
            >
              Retry
            </Button>
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Stack gap='xl'>
        <PageTitle
          title='Assessment Overview'
          description='Monitor and manage your assessment schedules'
        />

        {/* Filter button opens a modal containing the filter controls */}
        <Group>
          <Button
            variant='default'
            onClick={() => setFilterModalOpened(true)}
            leftSection={<IconFilter size={16} />}
          >
            Filter
          </Button>
        </Group>

        <Modal
          opened={filterModalOpened}
          onClose={() => setFilterModalOpened(false)}
          title='Filter Assessments'
          size={isMobile ? 'full' : 'lg'}
          fullScreen={isMobile}
          radius={isMobile ? 0 : 'md'}
          yOffset={isMobile ? 0 : undefined}
        >
          <OverviewFilter
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters(newFilters);
            }}
            onApply={(nextFilters) => {
              handleApplyFilters(nextFilters);
              setFilterModalOpened(false);
            }}
            isLoading={isLoading}
          />
        </Modal>

        {isLoading && <OverviewSkeleton />}

        {error && !isLoading && (
          <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red'>
            Failed to load overview data. Please try again later.
            <Button variant='light' size='sm' onClick={() => refetch} ml='md'>
              Retry
            </Button>
          </Alert>
        )}

        {overviewData && !isLoading && !error && (
          <>
            <Card>
              <Stack gap='md'>
                <Text fw={600} size='lg' c='dark'>
                  Assessment Status Participant & Respondent
                </Text>
                <SimpleGrid cols={{ base: 2, md: 4 }} spacing='md'>
                  <StatCard
                    title='Total Participants'
                    value={overviewData.stats.totalParticipants}
                    icon={<IconUsers size={16} />}
                    color='blue'
                  />
                  <StatCard
                    title='Completed Participants'
                    value={overviewData.stats.totalCompletedParticipants}
                    total={overviewData.stats.totalParticipants}
                    icon={<IconUsers size={16} />}
                    color='green'
                  />
                  <StatCard
                    title='Total Respondents'
                    value={overviewData.stats.totalRespondents}
                    icon={<IconClipboardList size={16} />}
                    color='teal'
                  />
                  <StatCard
                    title='Completed Respondents'
                    value={overviewData.stats.totalCompletedRespondents}
                    total={overviewData.stats.totalRespondents}
                    icon={<IconClipboardList size={16} />}
                    color='violet'
                  />
                </SimpleGrid>
              </Stack>
            </Card>

            {/* Enhanced Assessment Status Summary with clickable cards */}
            <Card>
              <Stack gap='md'>
                <Text fw={600} size='lg' c='dark'>
                  Assessment Status Summary
                </Text>
                <SimpleGrid cols={{ base: 2, md: 3 }} spacing='md'>
                  <StatCard
                    title='Planned'
                    value={overviewData.stats.plannedCount}
                    icon={<IconCalendarClock size={16} />}
                    color='blue'
                    clickable
                    onClick={() => {
                      handleStatusCardClick('Planned');
                      setSelectedType(null);
                      setIsTypeFilterPressed(false);
                    }}
                  />
                  <StatCard
                    title='On Going'
                    value={overviewData.stats.onGoingCount}
                    icon={<IconCalendarCheck size={16} />}
                    color='green'
                    clickable
                    onClick={() => {
                      handleStatusCardClick('On Going');
                      setSelectedType(null);
                      setIsTypeFilterPressed(false);
                    }}
                  />
                  <StatCard
                    title='Completed'
                    value={overviewData.stats.completedCount}
                    icon={<IconCalendarCheck size={16} />}
                    color='teal'
                    clickable
                    onClick={() => {
                      handleStatusCardClick('Completed');
                      setSelectedType(null);
                      setIsTypeFilterPressed(false);
                    }}
                  />
                </SimpleGrid>
                <SimpleGrid cols={{ base: 2, md: 3 }} spacing='md'>
                  <StatCard
                    title='Incomplete'
                    value={overviewData.stats.incompleteCount}
                    icon={<IconCalendarX size={16} />}
                    color='red'
                    clickable
                    onClick={() => {
                      handleStatusCardClick('Incomplete');
                      setSelectedType(null);
                      setIsTypeFilterPressed(false);
                    }}
                  />
                  <StatCard
                    title='Product Type'
                    value={overviewData.stats.totalTypeProduct}
                    icon={<IconTrendingUp size={16} />}
                    color='indigo'
                    clickable
                    onClick={() => {
                      setSelectedType('Product');
                      setIsTypeFilterPressed(true);
                      handleStatusCardClick('All');
                    }}
                  />
                  <StatCard
                    title='Non-Product Type'
                    value={overviewData.stats.totalTypeNonProduct}
                    icon={<IconTrendingUp size={16} />}
                    color='grape'
                    clickable
                    onClick={() => {
                      setSelectedType('Non-Product');
                      setIsTypeFilterPressed(true);
                      handleStatusCardClick('All');
                    }}
                  />
                </SimpleGrid>
              </Stack>
            </Card>

            <Grid>
              <Grid.Col span={12}>
                <AssessmentTable
                  title='List Assessment'
                  showProgress={true}
                  currentPage={assessmentListPage}
                  onPageChange={setAssessmentListPage}
                  showStatusFilter={true}
                  showDownload={true}
                />
              </Grid.Col>
            </Grid>

            {overviewData.comparisonAnalyses && overviewData.comparisonAnalyses.length > 0 && (
              <>
                <Divider my='xl' />
                <Card>
                  <Stack gap='md'>
                    <Text size='lg' fw={600}>
                      Before vs After Analysis
                    </Text>
                    <ComparisonAnalysisList
                      comparisons={overviewData.comparisonAnalyses}
                      onViewDetails={handleViewComparison}
                    />
                  </Stack>
                </Card>
              </>
            )}
          </>
        )}

        {/* Comparison Detail Modal */}
        <ComparisonDetailModal
          opened={comparisonModalOpened}
          onClose={() => {
            setComparisonModalOpened(false);
            setComparisonData(null);
            setSelectedComparison(null);
          }}
          comparisonData={comparisonData}
          isLoading={isLoadingComparison}
        />

        {/* Enhanced CSS for hover effects and clickable cards */}
        <style>{`
          .stat-card-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
            border-color: #d0d7de;
          }
          
          .clickable-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
            border-color: #007fff;
          }
          
          .clickable-card {
            transition: all 0.3s ease;
          }
          
          .clickable-card:hover::after {
            content: 'Click to filter';
            position: absolute;
            top: 8px;
            right: 8px;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 10px;
            z-index: 10;
          }
        `}</style>
      </Stack>
    </Container>
  );
};

export default LIAOverview;
